import { useCallback, useRef, useState } from 'react';
import {
  createFormStateFromQuery,
  validateForm,
} from '../reducers/searchQuery';
import { LocationType } from '../constants';

/** Manages draft form state before Redux commit (dual-state pattern). */
const useSearchFormState = currentQuery => {
  const [selectedServiceType, setSelectedServiceType] = useState(null);

  const [draftFormState, setDraftFormState] = useState(() =>
    createFormStateFromQuery(currentQuery),
  );

  const submitErrorsRef = useRef({});

  const setSubmitErrors = useCallback(errors => {
    submitErrorsRef.current = errors;
  }, []);

  const clearSubmitErrors = useCallback(() => {
    submitErrorsRef.current = {};
  }, []);

  const updateDraftState = useCallback(updater => {
    setDraftFormState(prev => {
      const updates =
        typeof updater === 'function' ? updater(prev) : { ...prev, ...updater };
      const flags = validateForm(prev, updates);
      const submitErrors = submitErrorsRef.current;

      // Prune submit errors for fields that now hold a value, so the error
      // disappears as soon as the user corrects the field (VACMS-24244,
      // refining VACMS-20271).
      if (
        submitErrors.locationChanged &&
        updates.searchString?.trim().length > 0
      ) {
        delete submitErrors.locationChanged;
      }
      // Clear the invalid-location error once the user types new input (#22751).
      if (
        submitErrors.locationInvalid &&
        updates.searchString !== undefined &&
        updates.searchString !== prev.searchString
      ) {
        delete submitErrors.locationInvalid;
        // Not managed by validateForm, so clear the stale draft key too.
        delete updates.locationInvalid;
      }
      if (submitErrors.facilityTypeChanged && updates.facilityType) {
        delete submitErrors.facilityTypeChanged;
      }
      if (
        submitErrors.serviceTypeChanged &&
        updates.facilityType === LocationType.CC_PROVIDER &&
        updates.serviceType?.length > 0
      ) {
        delete submitErrors.serviceTypeChanged;
      }
      // Also retire a stale service-type error when the user moves away
      // from CC_PROVIDER, since the field no longer applies.
      if (
        prev.facilityType !== updates.facilityType &&
        prev.facilityType === LocationType.CC_PROVIDER
      ) {
        delete submitErrors.serviceTypeChanged;
      }

      // Re-apply *Changed flags for submit errors that survived the prune
      // so a still-empty field keeps showing its error when an unrelated
      // field is edited (VACMS-22523 invariant).
      if (submitErrors.locationChanged) {
        flags.locationChanged = true;
      }
      if (submitErrors.locationInvalid) {
        flags.locationInvalid = true;
      }
      if (submitErrors.facilityTypeChanged) {
        flags.facilityTypeChanged = true;
      }
      if (
        submitErrors.serviceTypeChanged &&
        updates.facilityType === LocationType.CC_PROVIDER
      ) {
        flags.serviceTypeChanged = true;
      }

      const submitErrorFlags = {
        submitErrorLocation: !!submitErrors.locationChanged,
        submitErrorLocationInvalid: !!submitErrors.locationInvalid,
        submitErrorFacilityType: !!submitErrors.facilityTypeChanged,
        submitErrorServiceType: !!(
          submitErrors.serviceTypeChanged &&
          updates.facilityType === LocationType.CC_PROVIDER
        ),
      };

      return { ...updates, ...flags, ...submitErrorFlags };
    });
  }, []);

  const handleFacilityTypeChange = useCallback(
    e => {
      updateDraftState(prev => ({
        ...prev,
        facilityType: e.target.value,
        serviceType: null,
        vamcServiceDisplay: null,
        serviceTypeIntent: null,
        selectionMethod: null,
      }));
    },
    [updateDraftState],
  );

  const handleServiceTypeChange = useCallback(
    ({ target, selectedItem }) => {
      setSelectedServiceType(selectedItem);
      const option = target.value.trim();
      const serviceType = option === 'All' ? null : option;
      updateDraftState({ serviceType });
    },
    [updateDraftState],
  );

  return {
    draftFormState,
    setDraftFormState,
    updateDraftState,
    handleFacilityTypeChange,
    handleServiceTypeChange,
    selectedServiceType,
    setSelectedServiceType,
    setSubmitErrors,
    clearSubmitErrors,
  };
};

export default useSearchFormState;
